import { createOrganizationSchema, slugify, type CreateOrganizationInput } from '@church/domain'
import { submitSafely, useCreateOrganization, useSession, useSignOut } from '@church/data'
import { faChurchDuotone, faPlus, faRightFromBracket } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { Button, ErrorNote, Field, Icon, Input } from '../components/ui.tsx'
import { currentSite, urlOn } from '../lib/site.ts'

/**
 * First-run: create the church. Reached automatically when a signed-in user
 * has no organization; also linked from the org switcher for multi-church
 * users starting another one.
 */
export const Route = createFileRoute('/_authed/onboarding')({
  // A platform operator with no church of their own belongs in the console.
  beforeLoad: ({ context }) => {
    if (context.me.platformOperator && context.me.memberships.length === 0)
      throw redirect({ to: '/apps' })
  },
  component: OnboardingScreen,
})

function OnboardingScreen() {
  const navigate = useNavigate()
  const { me } = useSession()
  const createOrg = useCreateOrganization()
  const signOut = useSignOut()

  const form = useForm<CreateOrganizationInput>({
    resolver: zodResolver(createOrganizationSchema),
  })

  const submit = form.handleSubmit(async (input) => {
    await createOrg.mutateAsync(input)
    // On id.forhim.io the new church opens in ForChurch.
    if (currentSite() === 'id') window.location.assign(urlOn('church', '/'))
    else await navigate({ to: '/' })
  })

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-md space-y-6 rounded-xl border border-border-subtle bg-surface-raised p-6 shadow-sm sm:p-8">
        <div>
          <span className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-brand-soft text-content-brand">
            <Icon icon={faChurchDuotone} className="size-6" />
          </span>
          <h1 className="text-xl font-semibold text-content-default">
            Welcome{me ? `, ${me.user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="mt-1 text-sm text-content-muted">
            Set up your church to get started. You&rsquo;ll be its owner and can invite the rest of
            the team afterwards.
          </p>
        </div>

        <form onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
          <Field label="Church name" error={form.formState.errors.name?.message}>
            <Input
              placeholder="Grace Community Church"
              {...form.register('name', {
                onChange: (e: { target: { value: string } }) => {
                  if (!form.formState.dirtyFields.slug) {
                    form.setValue('slug', slugify(e.target.value))
                  }
                },
              })}
            />
          </Field>

          <Field label="URL identifier" error={form.formState.errors.slug?.message}>
            <Input placeholder="grace-community" {...form.register('slug')} />
          </Field>

          <ErrorNote error={createOrg.error} />

          <Button type="submit" icon={faPlus} className="w-full" disabled={createOrg.isPending}>
            {createOrg.isPending ? 'Creating…' : 'Create church'}
          </Button>
        </form>

        <button
          type="button"
          className="flex w-full items-center justify-center gap-1.5 text-sm text-content-muted hover:text-content-default"
          onClick={() => {
            signOut.mutate(undefined, {
              onSuccess: () => void navigate({ to: '/sign-in', search: { redirect: undefined } }),
            })
          }}
        >
          <Icon icon={faRightFromBracket} className="size-3.5" />
          Sign out
        </button>
      </div>
    </div>
  )
}
